import { useEffect, useRef } from "react";

import { createEditableTable, type EditableTableHandle } from "@/components/reader/editable-table";

/** Preview's editable table — a thin React mount for the shared grid in
 *  `editable-table.ts`. Live mode mounts the very same builder inside its
 *  CodeMirror widget, so the two modes cannot drift apart in look or behaviour. */
export function PreviewTable({
  source,
  onChange,
}: {
  /** The table's markdown, exactly as it appears in the note. */
  source: string;
  /** Replace that markdown with this. Called on commit, never per keystroke. */
  onChange: (next: string) => void;
}) {
  const host = useRef<HTMLDivElement>(null);
  const handle = useRef<EditableTableHandle | null>(null);
  // Kept in a ref so the grid — built once — always calls the CURRENT callback
  // rather than the one captured on the render that created it.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const el = host.current;
    if (!el) return;
    const built = createEditableTable(source, (next) => onChangeRef.current(next));
    if (!built) return;
    handle.current = built;
    el.appendChild(built.dom);
    return () => {
      built.dom.remove();
      handle.current = null;
    };
    // Built once per mount: later source changes flow through `sync` below, so
    // rebuilding would throw away the cell being typed in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handle.current?.sync(source);
  }, [source]);

  return <div ref={host} className="gm-editable-table-host" />;
}
